import { animate, state, style, transition, trigger } from '@angular/animations';
import { Clipboard } from '@angular/cdk/clipboard';
import { BreakpointObserver } from '@angular/cdk/layout';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SecurityContext,
  ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeHtml, SafeStyle } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { User } from '@vendasta/iam';
import { PostIdentifier, PostInterface, TheLoopService } from '@vendasta/the-loop';
import moment from 'moment';
import { Observable } from 'rxjs';
import { finalize, map, take } from 'rxjs/operators';

@Component({
  selector: 'sales-common-loop-v2-post',
  templateUrl: './post.component.html',
  styleUrls: ['./post.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('readMore', [
      state('collapsed', style({ maxHeight: '200px' })),
      state('expanded', style({ maxHeight: '5000px' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class PostComponent implements OnInit, AfterViewInit {
  @ViewChild('body', { read: ElementRef, static: false }) body: ElementRef;

  @Input() post: PostInterface;
  @Input() currentUser: User;
  @Input() compact = false;

  @Output() postClicked = new EventEmitter<PostIdentifier>();

  reacting = false;
  followingLoading = false;

  likeText = '';
  likeTooltip = '';
  hasLiked = false;
  isFollowing = false;
  safeHtml: SafeHtml;
  safeStyles: SafeStyle;
  expanded = false;
  expandable = false;
  readMoreAnimating = false;
  shareURL = '';

  postIconSize$: Observable<string>;
  commentIconSize$: Observable<string>;

  constructor(
    private readonly loopService: TheLoopService,
    private readonly translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private alertService: SnackbarService,
    private readonly sanitizer: DomSanitizer,
    private readonly breakpointObserver: BreakpointObserver,
    private clipboard: Clipboard,
  ) {}

  ngOnInit(): void {
    this.safeHtml = this.sanitizer.sanitize(SecurityContext.HTML, this.post?.html);
    this.safeStyles = this.post?.style ? this.sanitizer.bypassSecurityTrustHtml(this.post?.style) : null;
    this.computeValues();

    const breakpointObserver$ = this.breakpointObserver.observe(['(max-width: 600px)']);

    this.postIconSize$ = breakpointObserver$.pipe(map((result) => (result.matches ? '36' : '50')));
    this.commentIconSize$ = breakpointObserver$.pipe(map((result) => (result.matches ? '32' : '40')));
  }

  ngAfterViewInit(): void {
    this.calculateReadMore();

    const listener = (e) => {
      this.calculateReadMore();
      e.target.removeEventListener('load', listener);
    };

    const imgs = this.body.nativeElement.querySelectorAll('img');
    for (const img of imgs) {
      img.addEventListener('load', listener);
    }
  }

  calculateReadMore(): void {
    this.expandable = this.body.nativeElement.offsetHeight >= 200;

    this.cdr.detectChanges();
  }

  computeValues(): void {
    this.likeText = this.computeLikeText();
    this.hasLiked = this.computeHasLiked();
    this.isFollowing = this.computeIsFollowing();
    this.likeTooltip = this.computeLikeTooltip();
    this.shareURL = this.computeShareURL();
  }

  fromNow(date: Date): string {
    return moment(date).fromNow();
  }

  computeLikeText(): string {
    if ((this.post?.likes || []).length === 0) {
      return this.translate.instant('FRONTEND.LOOP.LIKE');
    }
    return (this.post?.likes || []).length + ' ' + this.translate.instant('FRONTEND.LOOP.LIKES');
  }

  computeLikeTooltip(): string {
    let tooltip = '';
    const likes = this.post?.likes || [];

    if (!likes.length) {
      return tooltip;
    }

    for (let i = 0; i < Math.min(likes.length, 10); i += 1) {
      const like = likes[i];
      if (like.firstName && !like.lastName) {
        tooltip += `${like.firstName}\n`;
        continue;
      }

      tooltip += `${like.firstName} ${like.lastName}\n`;
    }

    if (likes.length > 10) {
      tooltip += `\n${likes.length - 10} ${this.translate.instant('FRONTEND.LOOP.MORE_LIKES_TEXT')}`;
    }

    return tooltip;
  }

  computeHasLiked(): boolean {
    return (this.post?.likes || []).some((l) => l.userId === this.currentUser.userId);
  }

  computeIsFollowing(): boolean {
    return this.post?.isSubscribed || false;
  }

  computeShareURL(): string {
    const { protocol, host, pathname } = window.location;
    const { typeId, postId } = this.post?.postIdentifier ?? null;

    const typePath = `/type/${typeId}`;
    let shareUrl = `${protocol}//${host}${pathname}`;
    if (!pathname.includes(typePath)) {
      shareUrl += `${typePath}?postId=${encodeURIComponent(postId)}`;
    } else {
      shareUrl += `?postId=${encodeURIComponent(postId)}`;
    }

    return shareUrl;
  }

  reloadSubscription(identifier: PostIdentifier): void {
    this.followingLoading = true;
    this.loopService
      .getPost(identifier.typeId, identifier.postId)
      .pipe(
        take(1),
        finalize(() => {
          this.followingLoading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe(
        (post) => {
          this.post.isSubscribed = post.isSubscribed;
          this.computeValues();
          this.cdr.detectChanges();
        },
        (error) => {
          console.error(error);
        },
      );
  }

  like(): void {
    const { typeId, postId } = this.post?.postIdentifier ?? null;

    this.reacting = true;
    if (this.hasLiked) {
      this.loopService
        .unlike(typeId, postId)
        .pipe(
          take(1),
          finalize(() => {
            this.reacting = false;
            this.cdr.detectChanges();
          }),
        )
        .subscribe(
          () => {
            this.post.likes = (this.post.likes || []).filter((l) => l.userId !== this.currentUser.userId);
            this.computeValues();
            this.cdr.detectChanges();
          },
          (error) => {
            console.error(error);
            this.alertService.openErrorSnack('FRONTEND.LOOP.ERRORS.ERROR_UNLIKING_POST');
          },
        );
    } else {
      this.loopService
        .like(typeId, postId)
        .pipe(
          take(1),
          finalize(() => {
            this.reacting = false;
            this.cdr.detectChanges();
          }),
        )
        .subscribe(
          (user) => {
            this.post.likes = this.post.likes || [];
            this.post.likes.push(user);
            this.computeValues();
            this.reloadSubscription(
              new PostIdentifier({
                typeId,
                postId,
              }),
            );
            this.cdr.detectChanges();
          },
          (error) => {
            console.error(error);
            this.alertService.openErrorSnack('FRONTEND.LOOP.ERRORS.ERROR_LIKING_POST');
          },
        );
    }
  }

  follow(): void {
    this.followingLoading = true;
    this.loopService
      .subscribe(this.post?.postIdentifier.typeId, this.post?.postIdentifier.postId)
      .pipe(
        take(1),
        finalize(() => {
          this.followingLoading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe(
        () => {
          this.post.isSubscribed = true;
          this.computeValues();
          this.cdr.detectChanges();
        },
        (error) => {
          console.error(error);
          this.alertService.openErrorSnack('FRONTEND.LOOP.ERRORS.ERROR_FOLLOWING_POST');
        },
      );
  }

  unfollow(): void {
    this.followingLoading = true;
    this.loopService
      .unsubscribe(this.post?.postIdentifier.typeId, this.post?.postIdentifier.postId)
      .pipe(
        take(1),
        finalize(() => {
          this.followingLoading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe(
        () => {
          this.post.isSubscribed = false;
          this.computeValues();
          this.cdr.detectChanges();
        },
        (error) => {
          console.error(error);
          this.alertService.openErrorSnack('FRONTEND.LOOP.ERRORS.ERROR_UNFOLLOWING_POST');
        },
      );
  }

  share(): void {
    this.clipboard.copy(this.shareURL);
    this.alertService.openSuccessSnack('FRONTEND.LOOP.COPIED_TO_CLIPBOARD');
  }

  expand(): void {
    this.expanded = !this.expanded;
    this.cdr.detectChanges();
  }

  clickedPost(): void {
    this.postClicked.emit(new PostIdentifier(this.post.postIdentifier));
  }
}
